import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, ListSkeleton } from "@/components/common";
import { fmtDateTime, fromNow, initials } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Image as ImageIcon, Check, RefreshCw, X, Trash2, Upload, Send, MessageSquare, Loader2, CheckCircle2 } from "lucide-react";

const STATUS = {
  pending: { label: "Onay Bekliyor", chip: "bg-amber-100 text-amber-700 border-amber-200" },
  approved: { label: "Onaylandı", chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  changes_requested: { label: "Revizyon İstendi", chip: "bg-sky-100 text-sky-700 border-sky-200" },
  rejected: { label: "Reddedildi", chip: "bg-rose-100 text-rose-700 border-rose-200" },
};

function AuthImage({ path, className }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/files/${path}`, { responseType: "blob" });
        if (!alive) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      } catch {}
    })();
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  return src
    ? <img src={src} className={className} alt="tasarım" />
    : <div className={`${className} bg-muted animate-pulse grid place-items-center`}><ImageIcon className="h-6 w-6 text-muted-foreground" /></div>;
}

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd);
  return data;
}

export default function Designs() {
  const qc = useQueryClient();
  const { can, user } = useAuth();
  const canApprove = can("view_reports");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", file: null });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [decisionComment, setDecisionComment] = useState("");
  const [busy, setBusy] = useState(false);

  const { data = [], isLoading } = useQuery({ queryKey: ["designs"], queryFn: async () => { const d = (await api.get("/designs")).data; return Array.isArray(d) ? d : []; } });

  const create = async () => {
    if (!form.title.trim() || !form.file) { toast.error("Başlık ve görsel gerekli"); return; }
    setSaving(true);
    try {
      const up = await uploadFile(form.file);
      const { data: d } = await api.post("/designs", { title: form.title, description: form.description, file_id: up.id });
      toast.success("Tasarım onaya gönderildi");
      qc.invalidateQueries({ queryKey: ["designs"] });
      setCreateOpen(false); setForm({ title: "", description: "", file: null });
      setSelected(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openDesign = async (id) => { const { data: d } = await api.get(`/designs/${id}`); setSelected(d); setDecisionComment(""); };
  const remove = async (id) => { await api.delete(`/designs/${id}`); toast.success("Tasarım silindi"); qc.invalidateQueries({ queryKey: ["designs"] }); if (selected?.id === id) setSelected(null); };

  const decide = async (decision) => {
    setBusy(true);
    try { await api.post(`/designs/${selected.id}/decision`, { decision, comment: decisionComment }); toast.success("Karar kaydedildi"); await openDesign(selected.id); qc.invalidateQueries({ queryKey: ["designs"] }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendComment = async () => { if (!comment.trim()) return; await api.post(`/designs/${selected.id}/comment`, { body: comment }); setComment(""); await openDesign(selected.id); };
  const newVersion = async (file) => {
    setBusy(true);
    try { const up = await uploadFile(file); await api.post(`/designs/${selected.id}/version`, { file_id: up.id }); toast.success("Yeni versiyon yüklendi"); await openDesign(selected.id); qc.invalidateQueries({ queryKey: ["designs"] }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const latest = selected?.versions?.[selected.versions.length - 1];

  return (
    <div>
      <PageHeader title="Tasarım Onayları" subtitle="Grafik tasarımları yükleyin, inceleyin ve onaylayın"
        actions={<Button onClick={() => setCreateOpen(true)} data-testid="new-design-button"><Plus className="h-4 w-4 mr-1.5" /> Yeni Tasarım</Button>} />

      {isLoading ? <ListSkeleton /> : data.length === 0 ? (
        <EmptyState icon={ImageIcon} title="Henüz tasarım yok" description="Bir tasarım görseli yükleyip onay sürecini başlatın." action={<Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Yeni Tasarım</Button>} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.map((d) => {
            const st = STATUS[d.status] || STATUS.pending;
            const v = d.versions?.[d.versions.length - 1];
            return (
              <Card key={d.id} className="overflow-hidden group cursor-pointer hover:shadow-md transition-all" onClick={() => openDesign(d.id)} data-testid={`design-${d.id}`}>
                <div className="aspect-video bg-muted overflow-hidden">
                  {v && <AuthImage path={v.storage_path} className="w-full h-full object-cover" />}
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-sm truncate">{d.title}</h3>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button onClick={(e) => e.stopPropagation()} className="md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-rose-500 transition-opacity shrink-0"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader><AlertDialogTitle>Tasarımı sil</AlertDialogTitle><AlertDialogDescription>Bu tasarım kalıcı olarak silinsin mi?</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction onClick={() => remove(d.id)} data-testid="confirm-delete-button">Sil</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <Badge variant="outline" className={`text-[10px] ${st.chip}`}>{st.label}</Badge>
                    <span className="text-[10px] text-muted-foreground">v{d.versions?.length || 1}</span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="design-dialog">
          <DialogHeader><DialogTitle>Yeni Tasarım</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="design-title-input" /></div>
            <div><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={3} /></div>
            <div>
              <Label>Tasarım Görseli</Label>
              <label className="mt-1.5 flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer hover:bg-muted/50 transition-colors" data-testid="design-file-drop">
                <Upload className="h-6 w-6 text-muted-foreground mb-2" />
                <span className="text-sm text-muted-foreground">{form.file ? form.file.name : "Görsel seçin veya kamerayla çekin"}</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="design-file-input" />
              </label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>İptal</Button><Button onClick={create} disabled={saving} data-testid="design-save-button">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Gönder</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="design-detail-dialog">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2">{selected.title}<Badge variant="outline" className={`text-[10px] ${(STATUS[selected.status] || STATUS.pending).chip}`}>{(STATUS[selected.status] || STATUS.pending).label}</Badge></DialogTitle></DialogHeader>
              <p className="text-xs text-muted-foreground -mt-2">{selected.created_by_name} • {fromNow(selected.created_at)} • v{selected.versions?.length}</p>
              {selected.description && <p className="text-sm">{selected.description}</p>}

              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2 rounded-lg border overflow-hidden bg-muted">
                  {latest && <AuthImage path={latest.storage_path} className="w-full max-h-[420px] object-contain" />}
                </div>
                <div className="space-y-3">
                  {selected.decision_comment && (
                    <div className="text-xs bg-muted rounded-lg p-2.5"><span className="font-medium">{selected.decided_by_name}:</span> {selected.decision_comment}</div>
                  )}
                  {canApprove ? (
                    <div className="space-y-2">
                      <Textarea value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} placeholder="Karar notu (opsiyonel)" rows={2} data-testid="design-decision-comment" />
                      <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => decide("approved")} data-testid="design-approve"><Check className="h-4 w-4 mr-1.5" /> Onayla</Button>
                      <Button size="sm" variant="outline" className="w-full" disabled={busy} onClick={() => decide("changes_requested")} data-testid="design-request-changes"><RefreshCw className="h-4 w-4 mr-1.5" /> Revizyon İste</Button>
                      <Button size="sm" variant="outline" className="w-full text-rose-600" disabled={busy} onClick={() => decide("rejected")} data-testid="design-reject"><X className="h-4 w-4 mr-1.5" /> Reddet</Button>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Onay yetkisi yöneticilerdedir.</div>
                  )}
                  <label className="flex items-center justify-center gap-2 text-sm border rounded-lg p-2.5 cursor-pointer hover:bg-muted transition-colors" data-testid="design-new-version-label">
                    <Upload className="h-4 w-4" /> Yeni versiyon yükle
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && newVersion(e.target.files[0])} data-testid="design-new-version-input" />
                  </label>
                </div>
              </div>

              <div className="border-t pt-3">
                <div className="flex items-center gap-2 text-sm font-medium mb-2"><MessageSquare className="h-4 w-4" /> Yorumlar ({selected.comments?.length || 0})</div>
                <div className="space-y-3 max-h-40 overflow-y-auto mb-2">
                  {selected.comments?.map((c) => (
                    <div key={c.id} className="flex gap-2">
                      <Avatar className="h-7 w-7"><AvatarFallback className="text-[9px]">{initials(c.user_name)}</AvatarFallback></Avatar>
                      <div><p className="text-xs"><span className="font-medium">{c.user_name}</span> <span className="text-muted-foreground">{fromNow(c.created_at)}</span></p><p className="text-sm">{c.body}</p></div>
                    </div>
                  ))}
                  {(!selected.comments || selected.comments.length === 0) && <p className="text-sm text-muted-foreground">Henüz yorum yok.</p>}
                </div>
                <div className="flex gap-2">
                  <Input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()} placeholder="Yorum ekle..." data-testid="design-comment-input" />
                  <Button onClick={sendComment} size="icon" data-testid="design-comment-send"><Send className="h-4 w-4" /></Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
